import { useCallback, useRef } from 'react';
import { actionKey } from './actionKey';

// Story 65.37 — a trava de um pedido por vez para quem espera o resultado da gravação dentro do
// próprio manipulador (`await` e depois fechar a janela, avisar, seguir para o próximo passo) e
// para a gravação feita direto, sem hook. O segundo clique com a ação igual ainda a caminho não
// roda o manipulador de novo: recebe a mesma promessa do primeiro. Ação diferente (outra linha,
// outro valor) roda. O manipulador começa na hora, sem esperar: quem chama `preventDefault`
// continua chamando a tempo. O evento do clique não conta para dizer se a ação é a mesma.
export function useSingleFlightCallback<TArgs extends unknown[], TResult>(
  run: (...args: TArgs) => Promise<TResult> | TResult,
): (...args: TArgs) => Promise<TResult> {
  const latest = useRef(run);
  latest.current = run;
  const flights = useRef(new Map<string, Promise<TResult>>());
  return useCallback((...args: TArgs) => {
    const key = actionKey(args);
    const flying = flights.current.get(key);
    if (flying) return flying;
    let started: Promise<TResult>;
    try {
      started = Promise.resolve(latest.current(...args));
    } catch (error) {
      started = Promise.reject(error);
    }
    flights.current.set(key, started);
    const land = () => {
      if (flights.current.get(key) === started) flights.current.delete(key);
    };
    started.then(land, land);
    return started;
  }, []);
}

/**
 * A mesma trava para componentes em que os manipuladores nascem depois de um retorno
 * antecipado (onde um hook por manipulador não pode ser chamado): um só hook no topo, e cada
 * manipulador diz qual é a sua ação. `once(['pagar', forma], async () => { ... })` roda uma vez
 * enquanto a mesma ação estiver a caminho; o segundo chamado recebe a mesma promessa.
 */
export function useSingleFlightRunner(): <TResult>(action: unknown, run: () => Promise<TResult> | TResult) => Promise<TResult> {
  const flights = useRef(new Map<string, Promise<unknown>>());
  return useCallback(<TResult,>(action: unknown, run: () => Promise<TResult> | TResult): Promise<TResult> => {
    const key = actionKey(action);
    const flying = flights.current.get(key);
    if (flying) return flying as Promise<TResult>;
    let started: Promise<TResult>;
    try {
      started = Promise.resolve(run());
    } catch (error) {
      started = Promise.reject(error);
    }
    flights.current.set(key, started);
    const land = () => {
      if (flights.current.get(key) === started) flights.current.delete(key);
    };
    started.then(land, land);
    return started;
  }, []);
}
